import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './AssessmentTest.css';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5002';

export default function AssessmentTest() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading|error|intro|test|result
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [endsAt, setEndsAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // proctoring
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraGranted, setCameraGranted] = useState(false);
  const proctor = useRef({ tabSwitches: 0, copyPasteAttempts: 0, snapshotKeys: [] });
  const [warn, setWarn] = useState('');
  const submittedRef = useRef(false);

  // ---- load invite meta ----
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/assessments/test/${token}`);
        const d = await r.json();
        if (!r.ok || !d.success) { setError(d.message || 'Invalid link'); setPhase('error'); return; }
        setMeta(d); setPhase('intro');
      } catch { setError('Could not reach the assessment server.'); setPhase('error'); }
    })();
  }, [token]);

  const stopCamera = useCallback(() => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
  }, []);

  // ---- submit (also used by auto-submit) ----
  const submit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([questionId, selectedIndex]) => ({ questionId, selectedIndex })),
        tabSwitches: proctor.current.tabSwitches,
        copyPasteAttempts: proctor.current.copyPasteAttempts,
        cameraGranted,
        snapshotKeys: proctor.current.snapshotKeys,
        autoSubmitted: auto,
        mobile,
      };
      const r = await fetch(`${API_BASE}/api/assessments/test/${token}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      stopCamera();
      if (!r.ok || !d.success) { setError(d.message || 'Submission failed'); setPhase('error'); return; }
      setResult(d); setPhase('result');
    } catch {
      setError('Submission failed — check your connection.'); setPhase('error');
    } finally { setSubmitting(false); }
  }, [answers, cameraGranted, mobile, token, stopCamera]);

  // ---- timer ----
  useEffect(() => {
    if (phase !== 'test' || !endsAt) return;
    const tick = () => {
      const secs = Math.max(0, Math.round((new Date(endsAt).getTime() - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0) submit(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, endsAt, submit]);

  // ---- anti-cheat: tab switch + copy/paste + snapshots (only during test) ----
  useEffect(() => {
    if (phase !== 'test') return;
    const onHide = () => { if (document.hidden) { proctor.current.tabSwitches++; setWarn('⚠️ Leaving the test tab is recorded. Please stay on this page.'); } };
    const onBlur = () => { proctor.current.tabSwitches++; };
    const block = (e) => { e.preventDefault(); proctor.current.copyPasteAttempts++; setWarn('⚠️ Copy / paste is disabled during the test.'); };
    const onCtx = (e) => e.preventDefault();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('blur', onBlur);
    document.addEventListener('copy', block);
    document.addEventListener('paste', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', onCtx);

    // periodic proctoring snapshots
    const snap = setInterval(async () => {
      try {
        const v = videoRef.current;
        if (!v || !streamRef.current) return;
        const c = document.createElement('canvas');
        c.width = 320; c.height = 240;
        c.getContext('2d').drawImage(v, 0, 0, 320, 240);
        const image = c.toDataURL('image/jpeg', 0.5);
        const r = await fetch(`${API_BASE}/api/assessments/test/${token}/snapshot`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image }),
        });
        const d = await r.json();
        if (d.key) proctor.current.snapshotKeys.push(d.key);
      } catch { /* noop */ }
    }, 25000);

    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('copy', block);
      document.removeEventListener('paste', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', onCtx);
      clearInterval(snap);
    };
  }, [phase, token]);

  const warnTimer = useRef(null);
  useEffect(() => { if (!warn) return; clearTimeout(warnTimer.current); warnTimer.current = setTimeout(() => setWarn(''), 3500); }, [warn]);

  // ---- verify identity + camera, then start ----
  const startTest = async () => {
    if (!email.trim() || !mobile.trim()) { setError('Enter your email and mobile number.'); return; }
    setError(''); setVerifying(true);
    // ask for camera first (best-effort)
    let granted = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream; granted = true;
    } catch { granted = false; }
    setCameraGranted(granted);
    try {
      const r = await fetch(`${API_BASE}/api/assessments/test/${token}/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim(), mobile: mobile.trim() }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setError(d.message || 'Verification failed'); setVerifying(false); if (!granted) stopCamera(); return; }
      setQuestions(d.questions || []);
      setEndsAt(d.endsAt);
      setPhase('test');
      setTimeout(() => { if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current; }, 100);
    } catch { setError('Could not start the test.'); }
    finally { setVerifying(false); }
  };

  useEffect(() => () => stopCamera(), [stopCamera]);

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const answeredCount = Object.keys(answers).length;

  // ================= RENDER =================
  if (phase === 'loading') return <div className="at-wrap"><div className="at-card at-center"><div className="at-spinner" /><p>Loading assessment…</p></div></div>;

  if (phase === 'error') return (
    <div className="at-wrap"><div className="at-card at-center">
      <div className="at-emoji">🔒</div><h2>Assessment unavailable</h2><p className="at-muted">{error}</p>
    </div></div>
  );

  if (phase === 'intro') return (
    <div className="at-wrap"><div className="at-card">
      <div className="at-brand">Solitaire Overseas · Assessment</div>
      <h1>Hello{meta?.candidateName ? `, ${meta.candidateName}` : ''} 👋</h1>
      {meta?.jobTitle && <p className="at-sub">For the role of <b>{meta.jobTitle}</b></p>}
      <div className="at-rules">
        <div className="at-rule"><span>📝</span> <b>{meta?.questionCount}</b> multiple-choice questions</div>
        <div className="at-rule"><span>⏱️</span> <b>{Math.round((meta?.durationSeconds || 180) / 60)} minute(s)</b> — timer can’t be paused</div>
        <div className="at-rule"><span>🎥</span> Your <b>camera stays on</b> for the duration</div>
        <div className="at-rule"><span>🚫</span> No copy/paste, and <b>don’t switch tabs</b> (it’s recorded)</div>
        <div className="at-rule"><span>1️⃣</span> <b>Single attempt</b> — auto-submits when time runs out</div>
      </div>
      <div className="at-form">
        <label>Confirm your email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        <label>Mobile number</label>
        <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 …" />
      </div>
      {error && <div className="at-err">{error}</div>}
      <button className="at-btn" onClick={startTest} disabled={verifying}>
        {verifying ? 'Starting…' : 'Verify & Start Assessment'}
      </button>
      <p className="at-fine">By starting, you consent to camera-based proctoring for this assessment.</p>
    </div></div>
  );

  if (phase === 'result') return (
    <div className="at-wrap"><div className="at-card at-center">
      <div className="at-emoji">{result?.passed ? '🎉' : '📋'}</div>
      <h1>{result?.passed ? 'Great job!' : 'Assessment submitted'}</h1>
      <div className={`at-score ${result?.passed ? 'pass' : 'fail'}`}>{result?.percent}%</div>
      <p className="at-muted">{result?.correctCount}/{result?.totalQuestions} correct · pass mark {result?.passPercent}%</p>
      <p className="at-sub">{result?.passed
        ? 'You’ve cleared the assessment. Our team will reach out about the next steps.'
        : 'Thank you for taking the assessment. We’ll be in touch regarding your application.'}</p>
    </div></div>
  );

  // phase === 'test'
  const lowTime = remaining <= 30;
  return (
    <div className="at-test-wrap" onCopy={(e) => e.preventDefault()} onPaste={(e) => e.preventDefault()}>
      <div className={`at-timerbar ${lowTime ? 'low' : ''}`}>
        <div className="at-timer">⏱️ {mmss(remaining)}</div>
        <div className="at-progress">{answeredCount}/{questions.length} answered</div>
        <button className="at-submit" onClick={() => submit(false)} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit'}</button>
      </div>

      {warn && <div className="at-warn">{warn}</div>}

      <div className="at-questions">
        {questions.map((q, i) => (
          <div className="at-q" key={q._id}>
            <div className="at-q-head"><span className="at-q-num">{i + 1}</span><span className="at-q-cat">{q.category}</span></div>
            <div className="at-q-text">{q.text}</div>
            <div className="at-opts">
              {q.options.map((opt, idx) => (
                <label key={idx} className={`at-opt ${answers[q._id] === idx ? 'sel' : ''}`}>
                  <input type="radio" name={q._id} checked={answers[q._id] === idx} onChange={() => setAnswers(a => ({ ...a, [q._id]: idx }))} />
                  <span className="at-opt-letter">{String.fromCharCode(65 + idx)}</span>
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
        <button className="at-btn at-btn-final" onClick={() => submit(false)} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Finish & Submit'}
        </button>
      </div>

      {/* proctoring camera preview */}
      <div className="at-cam">
        {cameraGranted ? <video ref={videoRef} autoPlay muted playsInline /> : <div className="at-cam-off">📷 Camera off</div>}
        <span className="at-cam-dot" />
      </div>
    </div>
  );
}
